import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { inputIconWrapClass, inputIconInnerClass, labelClass } from './index'

const COUNTRY_OPTIONS = [
  { code: '+216', label: 'Tunisie', flag: '🇹🇳' },
  { code: '+33', label: 'France', flag: '🇫🇷' },
  { code: '+212', label: 'Maroc', flag: '🇲🇦' },
  { code: '+213', label: 'Algérie', flag: '🇩🇿' },
  { code: '+39', label: 'Italie', flag: '🇮🇹' },
  { code: '+34', label: 'Espagne', flag: '🇪🇸' },
  { code: '+44', label: 'Royaume-Uni', flag: '🇬🇧' },
  { code: '+49', label: 'Allemagne', flag: '🇩🇪' },
  { code: '+1', label: 'États-Unis', flag: '🇺🇸' },
  { code: '+971', label: 'Émirats', flag: '🇦🇪' },
  { code: '+32', label: 'Belgique', flag: '🇧🇪' },
  { code: '+41', label: 'Suisse', flag: '🇨🇭' },
  { code: '+31', label: 'Pays-Bas', flag: '🇳🇱' },
  { code: '+351', label: 'Portugal', flag: '🇵🇹' },
  { code: '+20', label: 'Égypte', flag: '🇪🇬' },
  { code: '+966', label: 'Arabie Saoudite', flag: '🇸🇦' },
  { code: '+974', label: 'Qatar', flag: '🇶🇦' },
  { code: '+90', label: 'Turquie', flag: '🇹🇷' },
  { code: '+1', label: 'Canada', flag: '🇨🇦' },
  { code: '+218', label: 'Libye', flag: '🇱🇾' },
]

const normalize = (value) =>
  (value || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const CountryPhoneInput = ({
  id = 'client_phone',
  countryCode = '+216',
  phone = '',
  onCountryCodeChange,
  onPhoneChange,
  label,
  required = false,
}) => {
  const { t } = useTranslation()
  const resolvedLabel = label ?? t('transferts.phone.label')
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const wrapperRef = useRef(null)
  const searchRef = useRef(null)

  const selected = useMemo(
    () => COUNTRY_OPTIONS.find((c) => c.code === (countryCode || '+216')) || COUNTRY_OPTIONS[0],
    [countryCode],
  )

  const filtered = useMemo(() => {
    const term = normalize(search.trim())
    if (!term) return COUNTRY_OPTIONS
    return COUNTRY_OPTIONS.filter(
      (c) => normalize(c.label).includes(term) || c.code.includes(term),
    )
  }, [search])

  useEffect(() => {
    if (!open) return undefined
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [open])

  useEffect(() => {
    if (open) {
      setSearch('')
      searchRef.current?.focus()
    }
  }, [open])

  const handleToggle = () => setOpen((prev) => !prev)

  const handleSelect = (code) => {
    onCountryCodeChange?.(code)
    setOpen(false)
  }

  const handleSelectorKeyDown = (event) => {
    if (event.key === 'Escape') setOpen(false)
    if ((event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') && !open) {
      event.preventDefault()
      setOpen(true)
    }
  }

  return (
    <div>
      <label className={labelClass} htmlFor={id}>
        {resolvedLabel}
        {required ? <span className='ml-1 text-red-500'>*</span> : null}
      </label>

      <div ref={wrapperRef} className='relative'>
        <div className={inputIconWrapClass}>
          <button
            type='button'
            onClick={handleToggle}
            onKeyDown={handleSelectorKeyDown}
            aria-haspopup='listbox'
            aria-expanded={open}
            aria-label={t('transferts.phone.countryAria')}
            className='-ml-1 flex shrink-0 items-center gap-1.5 rounded-md py-0.5 pl-1 pr-2 text-sm text-[var(--foreground)] hover:bg-[var(--muted)] focus:outline-none focus:ring-1 focus:ring-[var(--ring)]'
          >
            <span className='text-base leading-none'>{selected.flag}</span>
            <span className='font-medium'>{selected.code}</span>
            <svg
              className={`h-3.5 w-3.5 text-[var(--muted-foreground)] transition-transform ${open ? 'rotate-180' : ''}`}
              viewBox='0 0 20 20'
              fill='currentColor'
              aria-hidden='true'
            >
              <path
                fillRule='evenodd'
                d='M5.23 7.21a.75.75 0 011.06.02L10 11.17l3.71-3.94a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z'
                clipRule='evenodd'
              />
            </svg>
          </button>

          <span className='h-5 w-px shrink-0 bg-[var(--border)]' aria-hidden='true' />

          <input
            id={id}
            type='tel'
            className={inputIconInnerClass}
            value={phone}
            onChange={(e) => onPhoneChange?.(e.target.value)}
            placeholder={t('transferts.phone.numberPlaceholder')}
            autoComplete='tel-national'
          />
        </div>

        {open ? (
          <div className='absolute z-20 mt-1 w-72 max-w-[90vw] rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--card)] shadow-lg'>
            <div className='border-b border-[var(--border)] p-2'>
              <input
                ref={searchRef}
                type='text'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
                placeholder={t('transferts.phone.searchCountry')}
                aria-label={t('transferts.phone.searchCountry')}
                className='w-full rounded-md border border-[var(--border)] px-2.5 py-1.5 text-sm text-[var(--foreground)] placeholder:text-[var(--muted-foreground)] focus:border-[var(--ring)] focus:outline-none focus:ring-1 focus:ring-[var(--ring)]'
              />
            </div>
            <ul role='listbox' className='max-h-56 overflow-y-auto py-1'>
              {filtered.length === 0 ? (
                <li className='px-3 py-2 text-sm text-[var(--muted-foreground)]'>{t('transferts.phone.noResults')}</li>
              ) : (
                filtered.map((c) => (
                  <li key={`${c.code}-${c.label}`}>
                    <button
                      type='button'
                      role='option'
                      aria-selected={c.code === selected.code}
                      onClick={() => handleSelect(c.code)}
                      className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm hover:bg-[var(--muted)] ${
                        c.code === selected.code ? 'bg-[color-mix(in_oklab,var(--primary)_12%,transparent)] text-[var(--primary)]' : 'text-[var(--foreground)]'
                      }`}
                    >
                      <span className='text-base leading-none'>{c.flag}</span>
                      <span className='flex-1 truncate'>{c.label}</span>
                      <span className='shrink-0 text-[var(--muted-foreground)]'>{c.code}</span>
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default CountryPhoneInput
export { COUNTRY_OPTIONS }
